import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BoltIcon from '@mui/icons-material/Bolt';
import { McpIcon } from './icons';
import AccessStatusChip from './AccessStatusChip';
import { PortalServerOut as PortalServer } from '../types/apiRequests';
import { useTranslation } from '../i18n/useTranslation';

interface Props {
  server: PortalServer;
  isBeheerder: boolean;
  slotLimitReached: boolean;
  onNavigate: (name: string) => void;
  onRequestAccess: (server: PortalServer) => void;
  onInstantAdd?: (server: PortalServer) => void;
}

export default function ServerCard({ server, isBeheerder, slotLimitReached, onNavigate, onRequestAccess, onInstantAdd }: Props) {
  const t = useTranslation();
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <CardActionArea
        onClick={() => onNavigate(server.name)}
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          p: 0,
        }}
      >
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', p: 3, '&:last-child': { pb: 3 } }}>
          {/* Icon + Status row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: server.access_status === 'full_access' ? 'primary.main' : 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}
            >
              <McpIcon
                sx={{
                  fontSize: 20,
                  color: server.access_status === 'full_access' ? 'primary.contrastText' : 'text.secondary',
                }}
              />
            </Box>
            <AccessStatusChip status={server.access_status} />
          </Box>

          {/* Title */}
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1rem',
              letterSpacing: '-0.01em',
              mb: 0.25,
              lineHeight: 1.3,
            }}
            noWrap
          >
            {server.display_name}
          </Typography>

          {/* Application name */}
          {server.application_name && (
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.disabled',
                fontWeight: 500,
                mb: 0.5,
              }}
              noWrap
            >
              {server.application_name}
            </Typography>
          )}

          {/* Description */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 2.5,
              flexGrow: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              fontSize: '0.83rem',
            }}
          >
            {server.description || t('common.noDescription')}
          </Typography>

          {/* Stats row */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.disabled',
                fontWeight: 500,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {server.tool_count} tools
            </Typography>
            {(server.skill_count ?? 0) > 0 && (
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.disabled',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {server.skill_count} skills
              </Typography>
            )}
          </Box>
        </CardContent>
      </CardActionArea>

      {server.access_status === 'instant_available' && isBeheerder && (
        <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Tooltip
            title={slotLimitReached ? t('serverCard.slotLimitTooltip') : ''}
            disableHoverListener={!slotLimitReached}
          >
            <span style={{ display: 'block' }}>
              <Button
                variant="contained"
                size="small"
                fullWidth
                disabled={slotLimitReached}
                startIcon={<BoltIcon sx={{ fontSize: '16px !important' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onInstantAdd?.(server);
                }}
                sx={{
                  fontSize: '0.8rem',
                  py: 0.75,
                }}
              >
                {t('serverCard.instantAddButton')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}

      {server.access_status === 'not_linked' && isBeheerder && (
        <Box sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Tooltip
            title={slotLimitReached ? t('serverCard.slotLimitTooltip') : ''}
            disableHoverListener={!slotLimitReached}
          >
            <span style={{ display: 'block' }}>
              <Button
                variant="outlined"
                size="small"
                fullWidth
                disabled={slotLimitReached}
                endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestAccess(server);
                }}
                sx={{
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: 'primary.main',
                  fontSize: '0.8rem',
                  py: 0.75,
                  '&:hover': {
                    borderColor: 'primary.main',
                    bgcolor: 'rgba(0,0,0,0.02)',
                  },
                }}
              >
                {t('serverCard.activateButton')}
              </Button>
            </span>
          </Tooltip>
        </Box>
      )}
    </Card>
  );
}
